import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import type { ElementType } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { useAnalytics } from '@/hooks/useAnalytics';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { Eye, Users, Globe, Monitor, Smartphone, Tablet } from 'lucide-react';

export const Route = createFileRoute('/admin/analytics')({
  component: AdminAnalytics,
});

const COLORS = ['hsl(var(--primary))', 'hsl(var(--secondary))', 'hsl(var(--accent))', '#8884d8', '#82ca9d', '#ffc658'];

function StatCard({ title, value, icon: Icon, isLoading }: { title: string; value: number | string; icon: ElementType; isLoading: boolean }) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        {isLoading ? <Skeleton className="h-8 w-20" /> : <div className="text-2xl font-bold">{value}</div>}
      </CardContent>
    </Card>
  );
}

function DeviceIcon({ type }: { type: string }) {
  if (type === 'Mobile') return <Smartphone className="h-4 w-4" />;
  if (type === 'Tablet') return <Tablet className="h-4 w-4" />;
  return <Monitor className="h-4 w-4" />;
}

function AdminAnalytics() {
  const [timeRange, setTimeRange] = useState('30');
  const days = parseInt(timeRange);
  const { totalViews, uniqueSessions, pageStats, referrerStats, deviceStats, browserStats, timeSeriesData, isLoading } = useAnalytics(days);
  const avgPagesPerSession = uniqueSessions > 0 ? (totalViews / uniqueSessions).toFixed(1) : '0';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Statistiken</h1>
          <p className="text-muted-foreground mt-1">Übersicht über Besucherzahlen und Traffic-Quellen</p>
        </div>
        <Select value={timeRange} onValueChange={setTimeRange}>
          <SelectTrigger className="w-[180px]"><SelectValue placeholder="Zeitraum wählen" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Letzte 7 Tage</SelectItem>
            <SelectItem value="30">Letzte 30 Tage</SelectItem>
            <SelectItem value="90">Letzte 90 Tage</SelectItem>
            <SelectItem value="365">Letztes Jahr</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard title="Seitenaufrufe" value={totalViews.toLocaleString('de-DE')} icon={Eye} isLoading={isLoading} />
        <StatCard title="Eindeutige Besucher" value={uniqueSessions.toLocaleString('de-DE')} icon={Users} isLoading={isLoading} />
        <StatCard title="Seiten pro Besuch" value={avgPagesPerSession} icon={Globe} isLoading={isLoading} />
        <StatCard title="Top-Seiten" value={pageStats.length} icon={Monitor} isLoading={isLoading} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Seitenaufrufe über Zeit</CardTitle>
          <CardDescription>Tägliche Entwicklung der Besucherzahlen</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? <Skeleton className="h-[300px] w-full" /> : (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={timeSeriesData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="date" tickFormatter={(v) => new Date(v).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })} className="text-xs" />
                <YAxis className="text-xs" />
                <Tooltip labelFormatter={(v) => new Date(v).toLocaleDateString('de-DE')} formatter={(v: number) => [v, 'Aufrufe']} />
                <Line type="monotone" dataKey="views" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader><CardTitle>Top-Seiten</CardTitle><CardDescription>Meistbesuchte Seiten</CardDescription></CardHeader>
          <CardContent>
            {isLoading ? <div className="space-y-2">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
              : pageStats.length === 0 ? <p className="text-muted-foreground text-sm">Noch keine Daten vorhanden</p>
              : (
                <Table>
                  <TableHeader><TableRow><TableHead>Seite</TableHead><TableHead className="text-right">Aufrufe</TableHead><TableHead className="text-right">Besucher</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {pageStats.slice(0, 10).map((s) => (
                      <TableRow key={s.path}>
                        <TableCell className="font-medium">{s.path}</TableCell>
                        <TableCell className="text-right">{s.views}</TableCell>
                        <TableCell className="text-right">{s.uniqueSessions}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Traffic-Quellen</CardTitle><CardDescription>Woher kommen Ihre Besucher?</CardDescription></CardHeader>
          <CardContent>
            {isLoading ? <div className="space-y-2">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
              : referrerStats.length === 0 ? <p className="text-muted-foreground text-sm">Noch keine externen Verweise erfasst</p>
              : (
                <Table>
                  <TableHeader><TableRow><TableHead>Quelle</TableHead><TableHead className="text-right">Besuche</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {referrerStats.map((s) => (
                      <TableRow key={s.referrer}>
                        <TableCell className="font-medium">{s.referrer}</TableCell>
                        <TableCell className="text-right">{s.count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader><CardTitle>Geräteverteilung</CardTitle><CardDescription>Desktop, Mobile, Tablet</CardDescription></CardHeader>
          <CardContent>
            {isLoading ? <Skeleton className="h-[200px] w-full" />
              : deviceStats.length === 0 ? <p className="text-muted-foreground text-sm">Noch keine Daten vorhanden</p>
              : (
                <div className="flex items-center gap-8">
                  <ResponsiveContainer width="50%" height={200}>
                    <PieChart>
                      <Pie data={deviceStats} dataKey="count" nameKey="device_type" cx="50%" cy="50%" outerRadius={80}>
                        {deviceStats.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="space-y-2">
                    {deviceStats.map((s, i) => (
                      <div key={s.device_type} className="flex items-center gap-2">
                        <div className="h-3 w-3 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                        <DeviceIcon type={s.device_type} />
                        <span className="text-sm">{s.device_type}</span>
                        <span className="text-sm text-muted-foreground">({s.count})</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Browser</CardTitle><CardDescription>Verwendete Browser</CardDescription></CardHeader>
          <CardContent>
            {isLoading ? <Skeleton className="h-[200px] w-full" />
              : browserStats.length === 0 ? <p className="text-muted-foreground text-sm">Noch keine Daten vorhanden</p>
              : (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={browserStats} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis type="number" className="text-xs" />
                    <YAxis dataKey="browser" type="category" width={80} className="text-xs" />
                    <Tooltip />
                    <Bar dataKey="count" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
